import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Database } from '../types/supabase';
import { Calendar, TrendingUp, Users, FolderKanban, CheckSquare, Bot, Brain, ChevronDown } from 'lucide-react';
import { toast } from '../lib/toast';

type Agency = Database['public']['Tables']['agencies']['Row'];
type Project = Database['public']['Tables']['projects']['Row'];
type Task = Database['public']['Tables']['tasks']['Row'];

interface DashboardHighlightsProps {
  agencies: Agency[];
  projects: Project[];
  tasks: Task[];
}

interface ActivitySummary {
  newCustomers: number;
  newProjects: number;
  completedTasks: number;
  totalTasks: number;
  aiInteractions: number;
}

type TimeRange = 'daily' | 'weekly' | 'monthly';

export default function DashboardHighlights({ agencies, projects, tasks }: DashboardHighlightsProps) {
  const [selectedRange, setSelectedRange] = useState<TimeRange>('daily');
  const [summary, setSummary] = useState<ActivitySummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    calculateSummary();
  }, [agencies, projects, tasks, selectedRange]);

  const calculateSummary = async () => {
    try {
      const now = new Date();
      let startDate: Date;

      switch (selectedRange) {
        case 'daily':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'weekly':
          startDate = new Date(now.setDate(now.getDate() - now.getDay()));
          break;
        case 'monthly':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
      }

      // Get AI interactions count
      const { data: aiData } = await supabase
        .from('research_prompts')
        .select('count')
        .gte('created_at', startDate.toISOString())
        .single();

      const newSummary: ActivitySummary = {
        newCustomers: agencies.filter(a => new Date(a.created_at) >= startDate).length,
        newProjects: projects.filter(p => new Date(p.created_at) >= startDate).length,
        completedTasks: tasks.filter(t => t.completed && new Date(t.updated_at) >= startDate).length,
        totalTasks: tasks.filter(t => new Date(t.created_at) >= startDate).length,
        aiInteractions: parseInt(aiData?.count || '0')
      };

      setSummary(newSummary);
    } catch (error) {
      console.error('Error calculating summary:', error);
      toast.error('Failed to load activity summary');
    } finally {
      setLoading(false);
    }
  };

  const getRangeTitle = (range: TimeRange) => {
    switch (range) {
      case 'daily':
        return "Today's Activity";
      case 'weekly':
        return "This Week's Activity";
      case 'monthly':
        return "This Month's Activity";
    }
  };

  const getRangeIcon = (range: TimeRange) => {
    switch (range) {
      case 'daily':
        return <Calendar className="h-6 w-6 text-indigo-500 dark:text-indigo-400" />;
      case 'weekly':
        return <TrendingUp className="h-6 w-6 text-green-500 dark:text-green-400" />;
      case 'monthly':
        return <Bot className="h-6 w-6 text-purple-500 dark:text-purple-400" />;
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Activity Highlights</h2>
        <div className="relative">
          <select
            value={selectedRange}
            onChange={(e) => setSelectedRange(e.target.value as TimeRange)}
            className="appearance-none bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg py-2 pl-4 pr-10 text-sm font-medium text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="daily">Daily Activity</option>
            <option value="weekly">Weekly Activity</option>
            <option value="monthly">Monthly Activity</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {summary && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                {getRangeIcon(selectedRange)}
                <span className="ml-2">{getRangeTitle(selectedRange)}</span>
              </h3>
            </div>
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg">
                  <div className="flex items-center">
                    <Users className="h-5 w-5 text-indigo-500 dark:text-indigo-400" />
                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">New Customers</span>
                  </div>
                  <p className="mt-2 text-2xl font-semibold text-indigo-600 dark:text-indigo-400">
                    {summary.newCustomers}
                  </p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                  <div className="flex items-center">
                    <FolderKanban className="h-5 w-5 text-green-500 dark:text-green-400" />
                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">New Projects</span>
                  </div>
                  <p className="mt-2 text-2xl font-semibold text-green-600 dark:text-green-400">
                    {summary.newProjects}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                  <div className="flex items-center">
                    <CheckSquare className="h-5 w-5 text-purple-500 dark:text-purple-400" />
                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">Tasks Progress</span>
                  </div>
                  <p className="mt-2 text-2xl font-semibold text-purple-600 dark:text-purple-400">
                    {summary.completedTasks}/{summary.totalTasks}
                  </p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <div className="flex items-center">
                    <Brain className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">AI Usage</span>
                  </div>
                  <p className="mt-2 text-2xl font-semibold text-blue-600 dark:text-blue-400">
                    {summary.aiInteractions}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}